import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { User } from '@gigdriver/domain';
import {
  signUpWithEmail,
  signInWithEmail,
  signInWithGoogle,
  logout,
  onAuthChange,
} from '@gigdriver/client';
import { getUser, createUser } from '@gigdriver/client';

export const useAuthStore = defineStore('auth', () => {
  const currentUser = ref<User | null>(null);
  const firebaseUser = ref<any>(null);
  const isLoading = ref(true);
  const error = ref<string | null>(null);

  const isAuthenticated = computed(() => !!currentUser.value && !!firebaseUser.value);

  // Shared by initAuth's listener AND every explicit login/signup action
  // below — those actions apply this themselves (awaited, before their own
  // promise resolves) rather than leaving currentUser/firebaseUser to be
  // set later whenever onAuthStateChanged happens to fire. Relying on the
  // listener alone raced the post-login router.push: the login action could
  // resolve and navigate before the listener's own async profile lookup
  // finished, so the router guard still saw isAuthenticated as false and
  // bounced straight back to /login (most visible on Google sign-in, whose
  // popup+round-trip made the listener consistently lose that race).
  async function applyFirebaseUser(fb: any) {
    firebaseUser.value = fb;
    if (fb) {
      let user = await getUser(fb.uid);
      if (!user) {
        // First login - create user profile
        await createUser({
          uid: fb.uid,
          email: fb.email || '',
          displayName: fb.displayName || '',
          photoURL: fb.photoURL || '',
        });
        user = await getUser(fb.uid);
      }
      currentUser.value = user || null;
    } else {
      currentUser.value = null;
    }
  }

  async function initAuth() {
    return new Promise<void>(resolve => {
      onAuthChange(async fb => {
        await applyFirebaseUser(fb);
        isLoading.value = false;
        resolve();
      });
    });
  }

  async function signup(email: string, password: string, displayName: string) {
    try {
      error.value = null;
      const { user } = await signUpWithEmail(email, password);
      // Create user profile
      await createUser({
        uid: user.uid,
        email: user.email || '',
        displayName,
      });
      firebaseUser.value = user;
      currentUser.value = {
        uid: user.uid,
        email: user.email || '',
        displayName,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Signup failed';
      throw err;
    }
  }

  async function login(email: string, password: string) {
    try {
      error.value = null;
      const { user } = await signInWithEmail(email, password);
      await applyFirebaseUser(user);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Login failed';
      throw err;
    }
  }

  async function loginWithGoogle() {
    try {
      error.value = null;
      const { user } = await signInWithGoogle();
      await applyFirebaseUser(user);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Google login failed';
      throw err;
    }
  }

  async function logoutUser() {
    try {
      error.value = null;
      await logout();
      currentUser.value = null;
      firebaseUser.value = null;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Logout failed';
      throw err;
    }
  }

  return {
    currentUser,
    isAuthenticated,
    isLoading,
    error,
    initAuth,
    signup,
    login,
    loginWithGoogle,
    logout: logoutUser,
  };
});
